import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Spin } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { AUTH_ENDPOINTS } from '../../constants/api';
import { ROUTES } from '../../constants/routes';
import {
  LoginContainer,
  LoginCard,
  StyledForm,
  LoginHeader,
  Logo,
  BeatingHeart,
  StyledTitle,
  StyledAlert,
  SignInButton,
  FooterText,
  CopyrightText
} from '../AdminLogin/styled';

const { Text, Paragraph } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { token } = useParams();
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      try {
        const { data } = await axios.get(AUTH_ENDPOINTS.VERIFY_PASSWORD_TOKEN, {
          params: { token }
        });
        if (cancelled) return;
        if (data?.success) {
          setTokenValid(true);
          setEmail(data.email || '');
        } else {
          setTokenValid(false);
        }
      } catch (err) {
        if (cancelled) return;
        setTokenValid(false);
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };
    if (token) verify();
    else {
      setVerifying(false);
      setTokenValid(false);
    }
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async ({ password }) => {
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(AUTH_ENDPOINTS.RESET_PASSWORD, { token, password });
      if (data?.success) {
        setSuccess(true);
        setTimeout(() => navigate(ROUTES.LOGIN), 2500);
      } else {
        setError(data?.message || 'Unable to reset password.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Unable to reset password. The link may have expired.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <BeatingHeart />
          <Logo
            src="/bha-logo-full.png"
            alt="Black Heart Association"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <StyledTitle level={3}>Reset your password</StyledTitle>
          {email && !success && <Text type="secondary">for {email}</Text>}
        </LoginHeader>

        {verifying && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin /> <span style={{ marginLeft: 8 }}>Verifying link…</span>
          </div>
        )}

        {!verifying && !tokenValid && (
          <>
            <StyledAlert>
              <Alert
                message="Invalid or expired link"
                description="This password reset link is no longer valid. Please request a new one."
                type="error"
                showIcon
              />
            </StyledAlert>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to={ROUTES.FORGOT_PASSWORD}>Request a new link</Link>
            </div>
          </>
        )}

        {!verifying && tokenValid && success && (
          <Paragraph style={{ textAlign: 'center', marginTop: 16 }}>
            Your password has been reset. Redirecting you to sign in…
          </Paragraph>
        )}

        {!verifying && tokenValid && !success && (
          <>
            {error && (
              <StyledAlert>
                <Alert message={error} type="error" showIcon />
              </StyledAlert>
            )}
            <StyledForm
              form={form}
              name="reset-password"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 8, message: 'Password must be at least 8 characters' }
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="New password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirm"
                dependencies={['password']}
                hasFeedback
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    }
                  })
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm new password"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <SignInButton>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitting}
                    block
                    size="large"
                  >
                    {submitting ? 'Resetting…' : 'Reset Password'}
                  </Button>
                </SignInButton>
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Link to={ROUTES.LOGIN}>Back to sign in</Link>
              </div>
            </StyledForm>
          </>
        )}

        <FooterText>
          <CopyrightText type="secondary">
            © 2025 Black Heart Association. All rights reserved.
          </CopyrightText>
        </FooterText>
      </LoginCard>
    </LoginContainer>
  );
};

export default ResetPassword;
